import { PlaceData } from '@googlemaps/google-maps-services-js';

import { POSTAL_CODE_LENGTH_BY_COUNTRY, SupportedCountryCodes } from '@pflegia/types/common/constants/location';
import { PlaceType } from '@pflegia/types/common/enums/place-type.enum';
import { LocationObject } from '@pflegia/types/common/location/location-object';

type AddressComponent = { long_name: string; short_name: string; types: string[] };
type AddressGeometry = { location?: { lat: () => number; lng: () => number } };

export type FullAddress = {
  address_components: AddressComponent[];
  formatted_address: string;
  geometry: AddressGeometry;
  place_id: string;
  zone?: { lat: number; lng: number }[];
};
export type PlaceDataFullAddress = Pick<PlaceData, 'address_components' | 'formatted_address' | 'geometry' | 'place_id'>;

export type LocationCoordinate = LocationObject & { lat?: number; lng?: number; location?: { coordinates: number[] } };
export const parseLocationObject = (location: FullAddress | LocationCoordinate | string): LocationObject => {
  const geoLocation: FullAddress | LocationCoordinate = typeof location === 'string' ? JSON.parse(location) : location;

  return 'formatted_address' in geoLocation ? buildLocationObject(geoLocation) : extractCoordinatesFromLocation(geoLocation);
};

export const extractCoordinatesFromLocation = (item: LocationCoordinate): LocationObject => ({
  administrativeAreaLevel1: item?.administrativeAreaLevel1,
  administrativeAreaLevel2: item?.administrativeAreaLevel2,
  administrativeAreaLevel3: item?.administrativeAreaLevel3,
  administrativeAreaLevel4: item?.administrativeAreaLevel2,
  administrativeAreaLevel5: item?.administrativeAreaLevel2,
  city: item?.city,
  country: item?.country,
  countryCode: item?.countryCode,
  state: item?.state,
  formattedAddress: item?.formattedAddress,
  id: item?.id,
  lat: item?.lat ?? item?.location?.coordinates[1],
  lng: item?.lng ?? item?.location?.coordinates[0],
  placeId: item?.placeId,
  postalCode: item?.postalCode,
  streetAddressAndNumber: item?.streetAddressAndNumber,
  zone: item?.zone,
});

type ReducedLocationObject = {
  administrativeAreaLevel1: string | null;
  administrativeAreaLevel2: string | null;
  administrativeAreaLevel3: string | null;
  administrativeAreaLevel4: string | null;
  administrativeAreaLevel5: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  countryCode: SupportedCountryCodes | null;
  formattedAddress: string | null;
  lat: number | null;
  lng: number | null;
  placeId: string | null;
  postalCode: string | null;
  streetAddressAndNumber: string | null;
};

const baseLocationObject: ReducedLocationObject = {
  administrativeAreaLevel1: null,
  administrativeAreaLevel2: null,
  administrativeAreaLevel3: null,
  administrativeAreaLevel4: null,
  administrativeAreaLevel5: null,
  city: null,
  state: null,
  country: null,
  countryCode: null,
  formattedAddress: null,
  lat: null,
  lng: null,
  placeId: null,
  postalCode: null,
  streetAddressAndNumber: null,
};

const DEFAULT_COUNTRY_CODE: SupportedCountryCodes = 'DE';
const SINGLE_PART_ADDRESS_LENGTH = 1;
const TWO_PART_ADDRESS_LENGTH = 2;
const ADDRESS_WITH_COUNTRY_MIN_PARTS = 3;
const FIRST_ADDRESS_PART_INDEX = 0;
const SECOND_ADDRESS_PART_INDEX = 1;
const SECOND_LAST_PART_OFFSET = -2;
const STREET_PART_FROM_END_OFFSET = 3;
const POSSIBLE_POI_PREFIX = /^(hotel|hostel|klinikum?|krankenhaus|station|zentrum|center|campus)\b/i;

const getValidStreetAddress = (streetAddress?: string | null): string | null => {
  const trimmedStreetAddress = streetAddress?.trim();
  return trimmedStreetAddress ? trimmedStreetAddress : null;
};

const startsWithPostalCode = (text: string): boolean => /^\d{1,8}\b/.test(text);
const hasStreetNumber = (text: string): boolean => /\d/.test(text);
const isLikelyStreetAddress = (streetAddress: string): boolean =>
  hasStreetNumber(streetAddress) && !startsWithPostalCode(streetAddress) && !POSSIBLE_POI_PREFIX.test(streetAddress);

const parsePostalCodeAndCity = (postalCodeAndCity: string): Partial<Pick<LocationObject, 'city' | 'postalCode'>> => {
  const trimmedPostalCodeAndCity = postalCodeAndCity.trim();
  if (!trimmedPostalCodeAndCity) return {};

  const postalCodeAndCityMatch = trimmedPostalCodeAndCity.match(/^(\d{1,8})\s+(.+)$/);
  if (!postalCodeAndCityMatch) return { city: trimmedPostalCodeAndCity };

  return {
    city: postalCodeAndCityMatch[2].trim(),
    postalCode: postalCodeAndCityMatch[1],
  };
};

export const buildLocationObject = (address: FullAddress): LocationObject => {
  const addressComponentsReduced =
    address?.address_components?.reduce<ReducedLocationObject>((acc, curr) => {
      const updatedAcc = { ...acc };
      if (curr.types.includes(PlaceType.Locality)) {
        updatedAcc.city = curr.long_name;
      } else if (curr.types.includes(PlaceType.AdministrativeAreaLevel1)) {
        updatedAcc.administrativeAreaLevel1 = curr.long_name;
        updatedAcc.state = curr.long_name;
      } else if (curr.types.includes(PlaceType.Country)) {
        updatedAcc.country = curr.long_name;
        updatedAcc.countryCode = curr.short_name as SupportedCountryCodes;
      } else if (curr.types.includes(PlaceType.AdministrativeAreaLevel2)) {
        updatedAcc.administrativeAreaLevel2 = curr.long_name;
      } else if (curr.types.includes(PlaceType.AdministrativeAreaLevel3)) {
        updatedAcc.administrativeAreaLevel3 = curr.long_name;
      } else if (curr.types.includes(PlaceType.AdministrativeAreaLevel4)) {
        updatedAcc.administrativeAreaLevel4 = curr.long_name;
      } else if (curr.types.includes(PlaceType.AdministrativeAreaLevel5)) {
        updatedAcc.administrativeAreaLevel5 = curr.long_name;
      } else if (curr.types.includes(PlaceType.PostalCode)) {
        updatedAcc.postalCode = curr.long_name;
      } else if (curr.types.includes(PlaceType.StreetAddress) || curr.types.includes(PlaceType.Route)) {
        updatedAcc.streetAddressAndNumber = [
          curr.long_name,
          address.address_components.find(el => el.types.includes(PlaceType.StreetNumber))?.long_name,
        ]
          .filter(Boolean)
          .join(' ');
      }
      return updatedAcc;
    }, baseLocationObject) || baseLocationObject;

  const { countryCode, postalCode, streetAddressAndNumber, ...addressReduced } = addressComponentsReduced;

  if (!addressReduced.city) {
    const sublocality = address?.address_components?.find(
      component => component.types.includes(PlaceType.Sublocality) || component.types.includes(PlaceType.SublocalityLevel1),
    )?.long_name;
    addressReduced.city = sublocality ?? addressReduced.administrativeAreaLevel3;
  }

  const formattedAddress = address?.formatted_address;
  const parsedFormattedAddress = parseFormattedAddress(formattedAddress);

  const resolvedStreetAddress =
    getValidStreetAddress(streetAddressAndNumber) ?? getValidStreetAddress(parsedFormattedAddress?.streetAddressAndNumber);

  const resolvedCountryCode = countryCode ?? DEFAULT_COUNTRY_CODE;
  const isValidPostalCode = postalCode && postalCode.length >= POSTAL_CODE_LENGTH_BY_COUNTRY[resolvedCountryCode];
  const validFormattedAddress = isValidPostalCode ? formattedAddress : formattedAddress?.replace(postalCode as string, '').trim();

  return {
    ...addressReduced,
    streetAddressAndNumber: resolvedStreetAddress,
    formattedAddress: validFormattedAddress,
    placeId: address?.place_id,
    lat: address?.geometry?.location?.lat?.(),
    lng: address?.geometry?.location?.lng?.(),
    postalCode: isValidPostalCode ? postalCode : null,
    zone: address?.zone,
  } as LocationObject;
};

type AddressProperties = 'city' | 'country' | 'postalCode' | 'streetAddressAndNumber';

/**
 * Parse formattedAddress and return its parts.
 */
export const parseFormattedAddress = (address?: string): Partial<Pick<LocationObject, AddressProperties>> => {
  if (!address) return {};

  const result: Partial<Pick<LocationObject, AddressProperties>> = {};
  const addressParts = address
    .split(',')
    .map(text => text.trim())
    .filter(Boolean);

  if (!addressParts.length) return result;
  if (addressParts.length === SINGLE_PART_ADDRESS_LENGTH) {
    const country = addressParts[FIRST_ADDRESS_PART_INDEX];
    result.country = country;
    return result;
  }

  const [secondLastPart, lastPart] = addressParts.slice(SECOND_LAST_PART_OFFSET);

  if (addressParts.length === TWO_PART_ADDRESS_LENGTH && startsWithPostalCode(lastPart)) {
    const { city, postalCode } = parsePostalCodeAndCity(lastPart);
    const streetAddressAndNumber = getValidStreetAddress(secondLastPart);

    if (city) result.city = city;
    if (postalCode) result.postalCode = postalCode;
    if (streetAddressAndNumber && isLikelyStreetAddress(streetAddressAndNumber)) result.streetAddressAndNumber = streetAddressAndNumber;

    return result;
  }

  result.country = addressParts.length >= ADDRESS_WITH_COUNTRY_MIN_PARTS ? lastPart : addressParts[SECOND_ADDRESS_PART_INDEX];

  const postalCodeAndCityPart =
    addressParts.length >= ADDRESS_WITH_COUNTRY_MIN_PARTS ? secondLastPart : addressParts[FIRST_ADDRESS_PART_INDEX];
  const { city, postalCode } = parsePostalCodeAndCity(postalCodeAndCityPart);
  if (city) result.city = city;
  if (postalCode) result.postalCode = postalCode;

  if (addressParts.length >= ADDRESS_WITH_COUNTRY_MIN_PARTS) {
    const streetAddressAndNumber = getValidStreetAddress(addressParts[addressParts.length - STREET_PART_FROM_END_OFFSET]);
    if (streetAddressAndNumber && isLikelyStreetAddress(streetAddressAndNumber)) result.streetAddressAndNumber = streetAddressAndNumber;
  }

  return result;
};

/**
 * Format location object properties and return it in string format.
 */
export const formatAddress = (location: Partial<Pick<LocationObject, AddressProperties>>): string => {
  if (!location.country) return '';

  const parts = [];
  if (location.streetAddressAndNumber) parts.push(location.streetAddressAndNumber);
  if (location.postalCode && location.city) parts.push(`${location.postalCode} ${location.city}`);
  else if (location.city) parts.push(location.city);
  parts.push(location.country);
  return parts.join(', ');
};

export const convertPlaceDataToFullAddress = (placeData: PlaceDataFullAddress): FullAddress => ({
  ...placeData,
  geometry: {
    location: {
      lat: () => placeData.geometry.location.lat,
      lng: () => placeData.geometry.location.lng,
    },
  },
});

export const getLocationValue = (location?: Partial<LocationObject>) =>
  location?.city || location?.state || location?.administrativeAreaLevel1 || '';

export const isStateOnlyLocationObject = (location?: Partial<LocationObject>): boolean =>
  Boolean(!location?.city?.trim() && (location?.state?.trim() || location?.administrativeAreaLevel1?.trim()));

const CITY_STATE_CITIES = ['Berlin', 'Hamburg', 'Bremen'];

export const isCityStateLocationObject = (location?: Partial<LocationObject>): boolean => {
  const city = location?.city?.trim().toLowerCase();
  return Boolean(city && CITY_STATE_CITIES.some(cityState => cityState.toLowerCase() === city));
};
